// web/src/components/loader/CoverStack.tsx
// The energized cover, Figma-matched (node 2028:251): a −5°-rotated slide STACK — the front
// cover image plus two frosted translucent "slides" peeking up-and-right for dimensionality —
// with a #53a8ff blue glow that matches the beam. Position/size come from the STATIC loaderFx
// cover fields (tune live); shake amplitude + glow are the KEYFRAMED per-stage settings. The
// front layer is the source-thumb view-transition morph target from Create.
import { useEffect, useRef } from "react";
import type { JSX } from "react";
import { motion, useMotionValue, useTransform, useTime, animate, useReducedMotion } from "motion/react";
import type { MotionValue } from "motion/react";
import type { Mode } from "@/types/roast";
import { settingsForCharge, useLoaderFx } from "@/lib/loaderBeam";

const WHITE = "255,255,255"; // the whole glow — outer field + tight core

/** One shake axis for one layer: amplitude = keyframed cover-shake (a MotionValue) × an axis
 *  constant × an optional `ampScale` tier, plus a static `base` (the slide-stack offset).
 *  Module-level custom hook → called an unconditional, fixed number of times. */
function useShakeAxis(
  time: MotionValue<number>, ampMV: MotionValue<number>, reduced: boolean,
  phaseShift: number, axis: "x" | "y" | "rot", base = 0, ampScale = 1,
): MotionValue<number> {
  return useTransform([time, ampMV], ([t, amp]: number[]) => {
    if (reduced) return base;
    const a = amp * ampScale * (axis === "rot" ? 1.4 : axis === "x" ? 4 : 3);
    const osc = axis === "x" ? Math.sin((t + phaseShift) / 23) + Math.sin((t + phaseShift) / 37)
      : axis === "y" ? Math.cos((t + phaseShift) / 29) + Math.sin((t + phaseShift) / 19)
      : Math.sin((t + phaseShift) / 41);
    return base + osc * a;
  });
}

export function CoverStack({
  coverUrl, charge, phase,
}: { coverUrl: string | null; mode: Mode; charge: number; phase: "charging" | "ignite" }): JSX.Element {
  const reduced = useReducedMotion() ?? false;
  // Mobile tier (round 3): phones still dropped frames even after round 2 shrank the
  // shake to one layer — the GPU is saturated by the WebGL scene + blend-layer stack, not
  // just the amplitude. Coarse-pointer devices now render no halo/inner-glow blend layers
  // at all; the cover floats (drop-tolerant) and the border carries the energy instead.
  const coarse = typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(pointer: coarse)").matches
    : false;
  const fx = useLoaderFx();
  const s = settingsForCharge(charge, fx);
  const time = useTime();

  // Smoothly ease the keyframed shake-amp + glow toward the current-charge target.
  const shakeAmp = useMotionValue(0);
  const glow = useMotionValue(0);
  const flash = useMotionValue(0);
  useEffect(() => { const a = animate(shakeAmp, s.coverShake, { duration: 0.4 }); return () => a.stop(); }, [s.coverShake, shakeAmp]);
  useEffect(() => { const a = animate(glow, s.coverGlow, { duration: 0.4 }); return () => a.stop(); }, [s.coverGlow, glow]);
  useEffect(() => {
    if (phase !== "ignite") return;
    const a = animate(flash, [0, 1, 0], { duration: 0.6, ease: "easeOut" });
    return () => a.stop();
  }, [phase, flash]);

  // Fixed, unconditional hook calls (12): front (phase 0) + mid (500, +7/−8) + back (900, +15/−16)
  // + the inner glow's own copy of the front axes (phase 0, base 0) — same count, same order,
  // every render, on every tier (required: hooks can't be conditional). What differs is which
  // values actually reach `style` below. Desktop (fine pointer): fX/fY/fR drive the front's
  // shake, igX/igY/igR drive the inner glow, mid/back shake too. Mobile (coarse pointer):
  // mid/back freeze at their base offset (reduced || coarse); fX/fY/fR and igX/igY/igR are
  // still computed (hook-order requirement) but go unused — the front binds `floatY` +
  // border-energy instead (round 3), and the inner-glow layer isn't rendered at all.
  const frontAmpScale = coarse ? 0.6 : 1;
  const fX = useShakeAxis(time, shakeAmp, reduced, 0, "x", 0, frontAmpScale);
  const fY = useShakeAxis(time, shakeAmp, reduced, 0, "y", 0, frontAmpScale);
  const fR = useShakeAxis(time, shakeAmp, reduced, 0, "rot", 0, frontAmpScale);
  const mX = useShakeAxis(time, shakeAmp, reduced || coarse, 500, "x", 7);
  const mY = useShakeAxis(time, shakeAmp, reduced || coarse, 500, "y", -8);
  const mR = useShakeAxis(time, shakeAmp, reduced || coarse, 500, "rot");
  const bX = useShakeAxis(time, shakeAmp, reduced || coarse, 900, "x", 15);
  const bY = useShakeAxis(time, shakeAmp, reduced || coarse, 900, "y", -16);
  const bR = useShakeAxis(time, shakeAmp, reduced || coarse, 900, "rot");
  const igX = useShakeAxis(time, shakeAmp, reduced || coarse, 0, "x");
  const igY = useShakeAxis(time, shakeAmp, reduced || coarse, 0, "y");
  const igR = useShakeAxis(time, shakeAmp, reduced || coarse, 0, "rot");

  // Two SEPARATE plus-lighter white layers that add together: a broad outer glow + a tight
  // intense core. Both proportional to coverGlow (g=0 → nothing); `flash` adds the pop.
  const glowWide = useTransform([glow, flash], ([g, f]: number[]) =>
    `0 0 ${g * 82 + f * 30}px rgba(${WHITE},${g * 0.6 + f * 0.35}), ` +
    `0 0 ${g * 165 + f * 65}px rgba(${WHITE},${g * 0.45 + f * 0.3}), ` +
    `0 0 ${g * 260 + f * 95}px rgba(${WHITE},${g * 0.3 + f * 0.25})`);
  const glowCore = useTransform([glow, flash], ([g, f]: number[]) =>
    `0 0 ${g * 26 + f * 18}px ${g * 5}px rgba(${WHITE},${g * 1 + f * 0.5}), ` +
    `0 0 ${g * 62 + f * 34}px ${g * 2}px rgba(${WHITE},${g * 0.95 + f * 0.4})`);
  // Inner edge glow (inset) — light bleeding INWARD from the cover edges, plus-lighter over
  // the image, so the cover surface itself lights up as coverGlow rises (not just the halo).
  const innerGlow = useTransform([glow, flash], ([g, f]: number[]) =>
    `inset 0 0 ${g * 34 + f * 22}px rgba(${WHITE},${g * 0.5 + f * 0.3}), ` +
    `inset 0 0 ${g * 13 + f * 9}px rgba(${WHITE},${g * 0.72 + f * 0.35})`);
  // Brighten the whole cover harder as it intensifies → reads as lit, more believable.
  const filter = useTransform([glow, flash], ([g, f]: number[]) =>
    `brightness(${1 + g * 0.5 + f * 0.9}) saturate(${1 + g * 0.2})`);

  // Mobile tier (round 3): floats instead of shaking — a slow sine is drop-tolerant by
  // nature (a missed frame on a smooth curve is invisible, unlike shake jitter). The
  // float SPEEDS UP with charge (round 5, user-directed): ω scales with the eased
  // shakeAmp, capped at 1.8× so a dropped frame stays invisible at peak. The phase is
  // INTEGRATED (phase += dt·ω) rather than computed as sin(t/speed) — a changing
  // divisor would make the sine's argument jump and the card teleport at stage eases.
  // Frequency costs the GPU nothing; only drop-visibility caps it. Local custom hook:
  // called an unconditional, fixed 3× below (hook-order safe).
  const useFloat = (phase0: number, base: number, ampPx: number): MotionValue<number> => {
    const st = useRef({ lastT: 0, phase: phase0 });
    return useTransform([time, shakeAmp], ([t, amp]: number[]) => {
      if (reduced || !coarse) return base;
      const s = st.current;
      const dt = Math.max(0, t - s.lastT);
      s.lastT = t;
      s.phase += (dt * (1 + 0.8 * amp)) / 500; // calm ≈3.1s period → ≈1.75s at full charge
      return base + Math.sin(s.phase) * ampPx;
    });
  };
  // Cover + the two instances (round 4): phase-offset per layer, shallower toward the
  // back — the stack breathes with a parallax feel. Instances fold in their static base
  // offsets; x/rotate stay at their static bases via the coarse-frozen shake axes.
  const floatY = useFloat(0, 0, 7);
  const floatMidY = useFloat(0.9, -8, 5);
  const floatBackY = useFloat(1.8, -16, 4);
  // Mobile tier (round 3): the border carries the energy the removed glow halos used to —
  // calm cyan at rest, ramping to white-hot + a small tight glow on the same eased
  // glow/flash MotionValues everything else uses (updates only on the 0.4s stage eases).
  const mobileBorder = useTransform([glow, flash], ([g, f]: number[]) => {
    const m = Math.min(1, g + f);
    return `rgba(${Math.round(169 + (255 - 169) * m)}, ${Math.round(236 + (255 - 236) * m)}, 255, ${0.5 + 0.5 * m})`;
  });
  const mobileBorderGlow = useTransform([glow, flash], ([g, f]: number[]) =>
    `0 0 ${6 + g * 22 + f * 10}px rgba(255,255,255,${(g * 0.9 + f * 0.4).toFixed(3)})`);
  // The two frosted slides ALSO glow (Figma: all three layers carry the bloom), slightly under
  // the front — also proportional, so at g=0 the whole stack is glow-free.
  const size = coverUrl ? "block max-h-64 max-w-[27rem]" : "h-64 w-[27rem]";
  const base = `${size} rounded-[17px] border-2`;

  return (
    <div className="pointer-events-none absolute inset-0">
      {/* Wrapper: translate-centered on the tunable (coverX, coverY), rotated −5°, scaled. */}
      <div
        style={{
          position: "absolute",
          left: `calc(50% + ${fx.coverX}px)`,
          top: `${fx.coverY}%`,
          transform: `translate(-50%, -50%) rotate(${fx.coverRotate}deg) scale(${fx.coverScale})`,
        }}
      >
        {/* Mobile tier (round 3, coarse pointer ONLY — desktop below is byte-identical to
            before): two rounds of shrinking the shake still dropped frames at high charge —
            the phone GPU is saturated by the WebGL background PLUS these plus-lighter blend
            layers, and shake jitter makes every dropped frame visible. So on mobile we drop
            the halo-wide, halo-core, and inner-glow layers ENTIRELY (three fewer blend layers
            to composite — the actual GPU relief), swap the cover's shake for a slow float
            (a missed frame on a smooth sine is invisible, unlike jitter), and let the border
            carry the energy ramp that the halos used to (see mobileBorder/mobileBorderGlow
            above) — it only changes on the 0.4s stage eases, never per steady-state frame. */}
        {!coarse && (
          <>
            {/* Additive #53a8ff glow — a broad field + a tight intense core, both shadow-only
                layers that blend `plus-lighter` and pile up into a hot blue core. Behind the
                stack; sized to the cover via inset-0 so the halo follows its shape. */}
            {/* Halos track the front's shake (fX/fY/fR). They were frozen once as a mobile
                perf fix, but mobile no longer renders them at all — a frozen halo on desktop
                just reads as a static card stuck behind the shaking cover (user-reported). */}
            <motion.div
              aria-hidden
              data-testid="halo-wide"
              className={`absolute inset-0 rounded-[17px] ${size}`}
              style={{ x: fX, y: fY, rotate: fR, boxShadow: glowWide, mixBlendMode: "plus-lighter", zIndex: 0 }}
            />
            <motion.div
              aria-hidden
              data-testid="halo-core"
              className={`absolute inset-0 rounded-[17px] ${size}`}
              style={{ x: fX, y: fY, rotate: fR, boxShadow: glowCore, mixBlendMode: "plus-lighter", zIndex: 1 }}
            />
          </>
        )}
        {/* two frosted-WHITE "slides" behind, offset up-and-right. Filled bodies (unfilled,
            the dark WebGL scene read through them as dark-blue cards — user-reported);
            borderless now that the white fill defines their shape (round 5). */}
        <motion.div role="presentation" className={`absolute inset-0 bg-white/60 ${size} rounded-[17px]`} style={{ x: bX, y: coarse ? floatBackY : bY, rotate: bR, zIndex: 2 }} />
        <motion.div role="presentation" className={`absolute inset-0 bg-white/75 ${size} rounded-[17px]`} style={{ x: mX, y: coarse ? floatMidY : mY, rotate: mR, zIndex: 3 }} />
        {/* front — the cover image (in flow, defines the stack size); source-thumb morph target.
            Desktop keeps the x/y/rotate shake; mobile floats on `y` only (no x, no rotate — the
            wrapper's static −5° tilt stands in for it) and binds the border-energy styling
            instead of a glow halo. */}
        {coverUrl ? (
          <motion.img
            src={coverUrl} alt="" role="presentation" data-cover
            className={`relative z-[4] border-[rgba(169,236,255,0.5)] object-cover ${base}`}
            style={{
              y: floatY, filter, viewTransitionName: "source-thumb",
              ...(coarse ? { borderColor: mobileBorder, boxShadow: mobileBorderGlow } : { x: fX, y: fY, rotate: fR }),
            }}
          />
        ) : (
          <motion.div
            role="presentation" data-cover
            className={`relative z-[4] border-[rgba(169,236,255,0.5)] bg-white ${base}`}
            style={{
              y: floatY, filter, viewTransitionName: "source-thumb",
              ...(coarse ? { borderColor: mobileBorder, boxShadow: mobileBorderGlow } : { x: fX, y: fY, rotate: fR }),
            }}
          />
        )}
        {/* inner edge glow — sits over the cover, lights its edges inward as it intensifies.
            Own axes (igX/igY/igR) rather than sharing the front's fX/fY/fR: on desktop the
            front keeps shaking while everything else, inner glow included, tracks its own
            (unfrozen) copy of the same axes. Not rendered on mobile at all (see above). */}
        {!coarse && (
          <motion.div
            aria-hidden
            data-testid="inner-glow"
            className={`pointer-events-none absolute inset-0 z-[5] rounded-[17px] ${size}`}
            style={{ x: igX, y: igY, rotate: igR, boxShadow: innerGlow, mixBlendMode: "plus-lighter" }}
          />
        )}
      </div>
    </div>
  );
}
